import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Brain, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useSessionStore } from '../store/sessionStore';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../lib/i18n/context';
import { isValidLanguage } from '../lib/i18n/config';

export function JoinPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nickname, setNickname] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const setSession = useSessionStore((state) => state.setSession);
  const { t } = useTranslation();
  const location = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }

    const validateInvite = async () => {
      try {
        // Get invite details
        const { data: invite, error: inviteError } = await supabase
          .from('session_invites')
          .select('session_id, used_at, expires_at')
          .eq('token', token)
          .single();

        if (inviteError) {
          throw inviteError;
        }
        
        if (!invite) {
          throw new Error(t('session.invalid'));
        }

        // Check if invite is expired
        if (new Date(invite.expires_at) < new Date()) {
          throw new Error(t('session.expired'));
        }

        // Check if invite was already used
        if (invite.used_at) {
          throw new Error(t('session.expired'));
        }
        
        // Get session details
        const { data: session, error: sessionError } = await supabase
          .from('sessions')
          .select('*')
          .eq('id', invite.session_id)
          .single();

        if (sessionError) {
          throw sessionError;
        }
        
        if (!session) {
          throw new Error(t('session.invalid'));
        }
        
        if (!session.is_active) {
          throw new Error(t('session.expired'));
        }
        
        if (new Date(session.expires_at) < new Date()) {
          throw new Error(t('session.expired'));
        }

        setSession(session);
        setIsLoading(false);
      } catch (error) {
        console.error('Error validating invite:', error);
        setError(error instanceof Error ? error.message : t('session.invalid'));
        setIsLoading(false);
      }
    };

    validateInvite();
  }, [token, navigate, setSession, t]);

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !nickname.trim() || isJoining) return;
    setIsJoining(true);
    setError(null);

    try {
      // Get session ID from invite
      const { data: invite, error: inviteError } = await supabase
        .from('session_invites')
        .select('session_id')
        .eq('token', token)
        .single();

      if (inviteError) throw inviteError;
      if (!invite) throw new Error(t('session.invalid'));

      // Create participant first
      const { data: participant, error: participantError } = await supabase
        .from('participants')
        .insert({
          session_id: invite.session_id,
          nickname: nickname.trim()
        })
        .select()
        .single();

      if (participantError) throw participantError;
      if (!participant) throw new Error(t('common.error'));

      // Store participant in session store
      useSessionStore.getState().setParticipant(participant);

      // Mark invite as used only after participant is created
      const { error: updateError } = await supabase
        .from('session_invites')
        .update({ used_at: new Date().toISOString() })
        .eq('token', token);

      if (updateError) throw updateError;

      // Determine if we should include language prefix based on current path
      const pathParts = location.pathname.split('/').filter(Boolean);
      const hasLangPrefix = isValidLanguage(pathParts[0]);
      
      if (hasLangPrefix) {
        navigate(`/${language}/session/${invite.session_id}?join=true`);
      } else {
        navigate(`/session/${invite.session_id}?join=true`);
      }
    } catch (error) {
      console.error('Error joining session:', error);
      setError(error instanceof Error ? error.message : t('common.error'));
      setIsJoining(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="text-center">
          <Loader className="mx-auto h-12 w-12 animate-spin text-primary-500" />
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto max-w-md text-center">
          <Brain className="mx-auto h-16 w-16 text-gray-400" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            {error}
          </h2>
          <p className="mt-2 text-gray-600">
            {t('error.404.message')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 rounded-lg bg-primary-500 px-6 py-2 text-white hover:bg-primary-600 transition-colors"
          >
            {t('common.home')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mx-auto max-w-md">
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {t('session.join.title')}
          </h2>
          <form onSubmit={handleJoin} className="space-y-4">
            <div>
              <label htmlFor="nickname" className="block text-sm font-medium text-gray-700 mb-1">
                {t('home.nickname.label')}
              </label>
              <input
                type="text"
                id="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder={t('home.nickname.placeholder')}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isJoining || !nickname.trim()}
              className="w-full bg-primary-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
            >
              {isJoining ? t('common.loading') : t('session.join.button')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}